import { useIntl } from 'react-intl'

import { ConditionDto, FactNames } from '@marketplace-web/escrow-pricing/escrow-pricing-data'
import { formatCurrencyAmount } from '@marketplace-web/currency/currency-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { CountryModel } from '@marketplace-web/country/country-data'
import { CatalogNavigationModel } from '@marketplace-web/catalog/catalog-data'

import usePriceRuleConditionFormatter from './use-price-rule-condition-formatter'

type Props = {
  ruleConditions: Array<ConditionDto>
  countries?: Array<CountryModel> | null
  catalogs: Array<CatalogNavigationModel>
  currencyCode: string
}

type RuleDataRow = { label: string; value: string | Array<string> }

const usePricingRuleConditionsDataFormatter = ({
  ruleConditions,
  countries,
  catalogs,
  currencyCode,
}: Props) => {
  const { locale } = useIntl()
  const translate = useTranslate('available_discounts')

  const getCountryTitle = (countryCode: string) => {
    // Handle special case where 'uk' should map to 'GB' for the United Kingdom
    const normalizedCode = countryCode === 'uk' ? 'GB' : countryCode.toUpperCase()
    const matchedCountry = countries?.find(country => country.isoCode === normalizedCode)

    return matchedCountry ? matchedCountry.title : normalizedCode
  }

  const priceRuleData = [
    {
      value: usePriceRuleConditionFormatter({
        ruleConditions,
        factName: FactNames.CampaignAppliedCount,
      }),
      label: translate('price_rule_data.label.campaign_applied_count'),
    },
    {
      value: usePriceRuleConditionFormatter({
        ruleConditions,
        factName: FactNames.OrderValue,
        numberTransformer: val =>
          formatCurrencyAmount({ amount: String(val), currencyCode }, locale),
      }),
      label: translate('price_rule_data.label.order_value'),
    },
    {
      value: usePriceRuleConditionFormatter({
        ruleConditions,
        factName: FactNames.ContextCatalogIds,
        numberTransformer: val =>
          catalogs.find(catalog => catalog.id === Number(val))?.title || val,
      }),
      label: translate('price_rule_data.label.context_catalog_ids'),
    },
    {
      value: usePriceRuleConditionFormatter({
        ruleConditions,
        factName: FactNames.BuyerNthCarrierPurchase,
      }),
      label: translate('price_rule_data.label.buyer_nth_carrier_purchase'),
    },
    {
      value: usePriceRuleConditionFormatter({
        ruleConditions,
        factName: FactNames.BuyerNthPurchase,
      }),
      label: translate('price_rule_data.label.buyer_nth_purchase'),
    },
    {
      value: usePriceRuleConditionFormatter({
        ruleConditions,
        factName: FactNames.SellerNthSale,
      }),
      label: translate('price_rule_data.label.seller_nth_sale'),
    },
    {
      value: usePriceRuleConditionFormatter({
        ruleConditions,
        factName: FactNames.LastMileCarrierCode,
      }),
      label: translate('price_rule_data.label.last_mile_carrier_code'),
    },
    {
      value: usePriceRuleConditionFormatter({
        ruleConditions,
        factName: FactNames.PackageSizeCode,
        stringTransformer: val =>
          translate(`price_rule_data.value.package_sizes.names.${val.toLocaleLowerCase()}`),
      }),
      label: translate('price_rule_data.label.package_size_code'),
    },
    {
      value: usePriceRuleConditionFormatter({
        ruleConditions,
        factName: FactNames.DropOffType,
        stringTransformer: val => translate(`price_rule_data.value.drop_off_type.${val}`),
      }),
      label: translate('price_rule_data.label.drop_off_type'),
    },
    {
      value: usePriceRuleConditionFormatter({
        ruleConditions,
        factName: FactNames.SellerCountryCode,
        stringTransformer: getCountryTitle,
      }),
      label: translate('price_rule_data.label.seller_country_code'),
    },
    {
      value: usePriceRuleConditionFormatter({
        ruleConditions,
        factName: FactNames.BuyerCountryCode,
        stringTransformer: getCountryTitle,
      }),
      label: translate('price_rule_data.label.buyer_country_code'),
    },
    {
      value: usePriceRuleConditionFormatter({
        ruleConditions,
        factName: FactNames.FulfillmentServiceType,
        stringTransformer: val => translate(`price_rule_data.value.fulfilment_service_type.${val}`),
      }),
      label: translate('price_rule_data.label.fulfilment_service_type'),
    },
  ]

  return priceRuleData.filter(
    ruleData => ruleData.value !== null && ruleData.value?.length > 0,
  ) as Array<RuleDataRow>
}

export default usePricingRuleConditionsDataFormatter
